import React, { createContext, useContext, useCallback, useRef, PropsWithChildren } from 'react';
import { useAuth } from 'react-oidc-context';
import { toast } from 'sonner';

/**
 * Token Provider Context
 *
 * Provides a unified getAccessToken function that works differently based on deployment mode:
 * - Native Tab: Uses react-oidc-context with silent signin (OIDC configured with parent frame's identity URL)
 * - Standalone: Uses react-oidc-context with proper error handling and user prompts
 */

interface TokenProviderContextValue {
  getAccessToken: () => Promise<string>;
}

const TokenProviderContext = createContext<TokenProviderContextValue | null>(null);

/**
 * Native Tab Token Provider
 *
 * Uses react-oidc-context with silent signin for token management.
 * The AuthProvider is configured with the identity URL from the parent Access Workspace frame.
 * When token refresh fails, requests reauthentication from the parent frame.
 */
export function NativeTabTokenProvider({ children }: PropsWithChildren) {
  const auth = useAuth();
  const _getAccessTokenPromise = useRef<Promise<string> | null>(null);

  const getAccessToken = useCallback(async (): Promise<string> => {
    // Return cached promise if one is already in progress
    if (_getAccessTokenPromise.current) {
      return _getAccessTokenPromise.current;
    }

    _getAccessTokenPromise.current = (async () => {
      try {
        // Check if user exists and token is not expired
        if (auth.user && !auth.user.expired) {
          return auth.user.access_token;
        }

        // Token expired or user not available - try silent sign-in
        console.log('[NativeTabTokenProvider] Token expired, attempting silent renewal...');
        const user = await auth.signinSilent();
        if (user?.access_token) {
          console.log('[NativeTabTokenProvider] Silent renewal successful');
          return user.access_token;
        }

        throw new Error('Failed to get access token after silent signin');
      } catch (error: any) {
        console.error('[NativeTabTokenProvider] Error getting access token:', error);

        // Request reauthentication from parent frame
        try {
          const message = { type: "RequestReauthentication" };
          window.parent.postMessage(JSON.stringify(message), '*');
        } catch (postError) {
          console.error('[NativeTabTokenProvider] Failed to request reauthentication:', postError);
        }

        throw error;
      } finally {
        _getAccessTokenPromise.current = null;
      }
    })();

    return _getAccessTokenPromise.current;
  }, [auth]);

  return (
    <TokenProviderContext.Provider value={{ getAccessToken }}>
      {children}
    </TokenProviderContext.Provider>
  );
}

/**
 * Standalone Token Provider
 *
 * Uses react-oidc-context with proper error handling.
 * When silent renewal fails, shows a toast prompting the user to re-login.
 */
export function StandaloneTokenProvider({ children }: PropsWithChildren) {
  const auth = useAuth();
  const _getAccessTokenPromise = useRef<Promise<string> | null>(null);
  const _hasShownReauthToast = useRef(false);

  const getAccessToken = useCallback(async (): Promise<string> => {
    // Return cached promise if one is already in progress
    if (_getAccessTokenPromise.current) {
      return _getAccessTokenPromise.current;
    }

    _getAccessTokenPromise.current = (async () => {
      try {
        // Check if user exists and token is not expired
        if (auth.user && !auth.user.expired) {
          return auth.user.access_token;
        }

        // Token expired or user not available - try silent sign-in
        console.log('[StandaloneTokenProvider] Token expired, attempting silent renewal...');

        try {
          const user = await auth.signinSilent();
          if (user?.access_token) {
            console.log('[StandaloneTokenProvider] Silent renewal successful');
            _hasShownReauthToast.current = false; // Reset toast flag on success
            return user.access_token;
          }
        } catch (silentError: any) {
          console.warn('[StandaloneTokenProvider] Silent renewal failed:', silentError.message);

          // Show toast only once per session
          if (!_hasShownReauthToast.current) {
            _hasShownReauthToast.current = true;

            toast.error('Session expired', {
              description: 'Click to re-login',
              duration: 10000,
              action: {
                label: 'Re-login',
                onClick: () => {
                  auth.signinRedirect();
                },
              },
            });
          }

          throw new Error('Session expired - please re-login');
        }

        throw new Error('Failed to get access token');
      } finally {
        _getAccessTokenPromise.current = null;
      }
    })();

    return _getAccessTokenPromise.current;
  }, [auth]);

  return (
    <TokenProviderContext.Provider value={{ getAccessToken }}>
      {children}
    </TokenProviderContext.Provider>
  );
}

/**
 * Dev Auth Token Provider
 *
 * Used when VITE_DEV_AUTH=true. No OIDC — the backend DevAuthBypassMiddleware
 * injects claims directly, so the token value doesn't matter.
 */
export function DevAuthTokenProvider({ children }: PropsWithChildren) {
  const getAccessToken = useCallback(async (): Promise<string> => {
    return 'dev-auth-token';
  }, []);

  return (
    <TokenProviderContext.Provider value={{ getAccessToken }}>
      {children}
    </TokenProviderContext.Provider>
  );
}

/**
 * Hook to access the token provider
 */
export function useTokenProvider() {
  const context = useContext(TokenProviderContext);

  if (!context) {
    throw new Error('useTokenProvider must be used within a TokenProvider (NativeTabTokenProvider or StandaloneTokenProvider)');
  }

  return context;
}
